import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

type PortalInfo = {
  portalName: string;
  customerName: string;
};

async function errorMessageFromResponse(response: Response): Promise<string> {
  try {
    const data: unknown = await response.json();
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message;
    }
  } catch {
    // ignore
  }

  return `Request failed (${response.status})`;
}

export function PortalLoginPage() {
  const { portalId } = useParams({ from: '/portal/$portalId/login' });

  const [portalInfo, setPortalInfo] = useState<PortalInfo | null>(null);
  const [isLoadingPortalInfo, setIsLoadingPortalInfo] = useState(true);
  const [portalInfoError, setPortalInfoError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoadingPortalInfo(true);
      setPortalInfoError(null);

      const response = await fetch(`/portal/${portalId}/auth/portal-info`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const msg = await errorMessageFromResponse(response);
        if (!cancelled) setPortalInfoError(msg);
        if (!cancelled) setPortalInfo(null);
        if (!cancelled) setIsLoadingPortalInfo(false);
        return;
      }

      const data: PortalInfo = await response.json();
      if (!cancelled) setPortalInfo(data);
      if (!cancelled) setIsLoadingPortalInfo(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [portalId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!email.trim() || !password) {
      setFormError('Email and password are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`/portal/${portalId}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        setFormError(await errorMessageFromResponse(response));
        return;
      }

      window.location.href = `/portal/${portalId}`;
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Portal login</CardTitle>
          <CardDescription>
            {isLoadingPortalInfo
              ? 'Loading portal...'
              : portalInfo
                ? `${portalInfo.portalName} • ${portalInfo.customerName}`
                : 'Unable to load portal'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {portalInfoError && (
            <div className="text-sm text-destructive mb-4">
              {portalInfoError}
            </div>
          )}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="portal-email">Email</Label>
              <Input
                id="portal-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-password">Password</Label>
              <Input
                id="portal-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {formError && (
              <div className="text-sm text-destructive">{formError}</div>
            )}
            <Button
              className="w-full"
              type="submit"
              disabled={
                isSubmitting || isLoadingPortalInfo || !!portalInfoError
              }
            >
              {isSubmitting ? 'Logging in...' : 'Log in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
